import {
  Alert,
  Box,
  Button,
  Card,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { accountingRequest } from '../api'

// SND-14244: AR and AP summary for a month, like Gestion's month-end Interface sheet. Worked out on the server from
// the JDV, JRM, JDA and JDD rules each time; nothing is stored. The opening balance is everything before the month.
function currentMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function amount(value: unknown) {
  const number = Number(value) || 0
  const text = Math.abs(number).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return number < 0 ? `-${text}` : text
}

function SummaryTable({ title, side, columns, openingLabel, endingLabel }: {
  title: string
  side: any
  columns: { code: string; name: string }[]
  openingLabel: string
  endingLabel: string
}) {
  const keys = [...columns.map((column) => column.code), 'total']
  const money = (value: unknown, bold = false) => (
    <Typography
      variant="body2"
      component="span"
      sx={{ fontWeight: bold ? 700 : 400, color: Number(value) < 0 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}
    >
      {amount(value)}
    </Typography>
  )
  const balanceRow = (label: string, detail: string, values: any) => (
    <TableRow sx={{ bgcolor: 'rgba(233, 180, 76, 0.18)' }}>
      <TableCell />
      <TableCell colSpan={2}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </TableCell>
      {keys.map((key) => (
        <TableCell key={key} align="right">{money(values?.[key], true)}</TableCell>
      ))}
    </TableRow>
  )
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>{title}</Typography>
      <TableContainer component={Card} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {['', 'Line', 'Source', ...columns.map((column) => column.name), 'Total'].map((label, index) => (
                <TableCell key={`${label}-${index}`} align={index >= 3 ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0, width: index === 0 ? 32 : undefined }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {balanceRow('Prior Month Ending Balance', openingLabel, side.openingBalance)}
            {side.lines.map((line: any) => (
              <TableRow key={line.key} sx={{ opacity: line.pending ? 0.6 : 1 }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>{line.sign}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{line.english}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontStyle: line.pending ? 'italic' : 'normal' }}>{line.source}</Typography>
                </TableCell>
                {keys.map((key) => (
                  <TableCell key={key} align="right">{money(line.values?.[key])}</TableCell>
                ))}
              </TableRow>
            ))}
            {balanceRow('Current Ending Balance', endingLabel, side.endingBalance)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default function ArApSummary() {
  const [month, setMonth] = useState(currentMonth())
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function load(value: string) {
    setLoading(true)
    setError('')
    try {
      setData(await accountingRequest(`/reports/ar-ap?month=${encodeURIComponent(value)}`))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(currentMonth())
  }, [])

  const columns: { code: string; name: string }[] = data?.offices || []
  const openingLabel = data ? `Balance as of ${data.openingAsOf}${data.hasOpeningData ? '' : ' (no earlier data in Leo: 0)'}` : ''
  const endingLabel = data ? `Balance as of ${data.to}` : ''

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>AR / AP — Accounts Receivable and Payable</Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          type="month"
          label="Month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" color="warning" disabled={loading || !month} onClick={() => load(month)}>
          {loading ? 'Working it out…' : 'Show summary'}
        </Button>
      </Stack>
      {loading ? <LinearProgress color="warning" sx={{ mt: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {data && !loading ? (
        <>
          <SummaryTable title="Accounts Receivable" side={data.ar} columns={columns} openingLabel={openingLabel} endingLabel={endingLabel} />
          <SummaryTable title="Accounts Payable" side={data.ap} columns={columns} openingLabel={openingLabel} endingLabel={endingLabel} />
        </>
      ) : null}
    </Box>
  )
}
